import React from 'react'
import dayjs from 'dayjs'
import { Outlet, useNavigate, useParams } from 'react-router'
import {
  AsyncCampaignCard,
  AsyncSourceCard,
  AsyncStatsCard,
  Button,
  CampaignSuggestion,
  SourceSuggestion,
} from '@demandlocal/components'
import { useGetTotalCustomers } from '../../../../../api/dealership/profile/useGetTotalCustomers'
import { useGetInventoryTotals } from '../../../../../api/dealership/profile/useGetInventoryTotals'
import { useGetTotalAudiences } from '../../../../../api/dealership/profile/useGetTotalAudiences'
import { useGetTotalBudget } from '../../../../../api/dealership/profile/useGetTotalBudget'
import { useGetPrimaryKpi } from '../../../../../api/dealership/profile/useGetPrimaryKpi'
import { useGetPerformanceStats } from '../../../../../api/dealership/useGetPerformanceStats'
import { useGetProfileMetrics } from '../../../../../api/dealership/profile/useGetProfileMetrics'
import { useGetDealerProfileSources } from '../../../../../api/dealership/profile/useGetDealerProfileSources'
import { useGetPhoneCalls } from '../../../../../api/dealership/profile/useGetPhoneCalls'
import { ReactComponent as MultipleVehiclesIcon } from '../../../../../images/icons/multiple_vehicles.svg'
import { ReactComponent as VehicleIcon } from '../../../../../images/icons/vehicle.svg'
import { ReactComponent as ExpenseIcon } from '../../../../../images/icons/expense_icon.svg'
import MonthlyPerformanceCard from '../../../../../components/Overview/MonthlyPerformanceCard'
import { formatNumber } from '../../../../../lib/utils/formatNumber'
import { ImpressionLineChart } from '../../../../../components/Charts/Reports/ImpressionLineChart'
import { ClickLineChart } from '../../../../../components/Charts/Reports/ClickLineChart'
import { productCodeToVendorIcon } from '../../../../../lib/utils/mapVendorsToLogos'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import { useGetAllCampaignsByDealership } from '../../../../../api/dealership/campaigns/useGetAllCampaignsByDealership'

const loadingCampaign = {
  client_campaign_id: 0,
  client_campaign_display_name: '',
  vendor_id: 0,
  product_code: '',
  product_name: '',
  start_date: '',
  end_date: '',
  clicks: 0,
  impressions: 0,
  views: 0,
  audience_impressions: 0,
  vehicles: 0,
  ctr: 0,
  vtr: 0,
  leads: 0,
  calls: 0,
  demand_local_net: 0,
  budget: 0,
}

const loadingSource = { id: 0, title: '', last_updated: '', total_customers: 0 }

export default function DealerProfileWithDMS(): JSX.Element {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const navigate = useNavigate()

  const { agency_id } = useParams()
  const agency = useGetAgencyByAgencyId(agency_id)
  const { data: performanceStats, isLoading: isLoadingPerformanceStats } =
    useGetPerformanceStats({
      filters: {
        start_date: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD'),
      },
    })
  const { data: totalBudget = { data: 0, title: '' }, isLoading: isLoadingTotalBudget } =
    useGetTotalBudget({})
  const { data: totalCustomers = { data: 0 }, isLoading: isLoadingTotalCustomers } =
    useGetTotalCustomers()
  const { data: totalAudiences = { data: 0 }, isLoading: isLoadingTotalAudiences } =
    useGetTotalAudiences()
  const { data: primaryKpi = { kpi: '', data: 0 }, isLoading: isLoadingPrimaryKpi } =
    useGetPrimaryKpi()
  const { data: phoneCalls = { data: 0 }, isLoading: isLoadingPhoneCalls } =
    useGetPhoneCalls({})
  const {
    data: inventoryTotals = { total: 0, used: 0, new: 0 },
    isLoading: isLoadingInventoryTotals,
  } = useGetInventoryTotals()
  const { data: metricsData = { data: [] }, isLoading: isLoadingMetrics } =
    useGetProfileMetrics()

  const {
    data: campaignsList = { data: [loadingCampaign, loadingCampaign, loadingCampaign] },
    isLoading: isLoadingCampaigns,
  } = useGetAllCampaignsByDealership({
    filters: {
      startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    },
  })

  const {
    data: sourcesList = { sources: [loadingSource, loadingSource, loadingSource] },
    isLoading: isLoadingSources,
  } = useGetDealerProfileSources()

  return (
    <div className="flex flex-col gap-5 w-full font-Spartan px-10">
      <div className={`grid grid-cols-3 gap-5`}>
        <MonthlyPerformanceCard
          title="Units Sold"
          color="blue"
          icon={<MultipleVehiclesIcon className="opacity-50" />}
          isLoading={isLoadingPerformanceStats}
          content={
            <div className="flex flex-col gap-2 pt-4">
              <span className="text-xs font-bold">Past 30 Days</span>
              <span className="font-bold text-lg">
                {formatNumber(performanceStats?.unitsSold ?? 0)}
              </span>
            </div>
          }
        />
        <MonthlyPerformanceCard
          title="Influenced Sales"
          color="purple"
          icon={<VehicleIcon className="opacity-50" />}
          isLoading={isLoadingPerformanceStats}
          content={
            <div className="flex flex-col gap-2 pt-4">
              <span className="text-xs font-bold">Past 30 Days</span>
              <span className="font-bold text-lg">
                {formatNumber(performanceStats?.attributedSales ?? 0)}
              </span>
            </div>
          }
        />
        <MonthlyPerformanceCard
          title="Revenue from Influenced Sales"
          color="orange"
          icon={<ExpenseIcon className="opacity-50" />}
          isLoading={isLoadingPerformanceStats}
          content={
            <div className="flex flex-col gap-2 pt-4">
              <span className="text-xs font-bold">Past 30 Days</span>
              <span className="font-bold text-lg">
                ${formatNumber(performanceStats?.attributedRevenue ?? 0)}
              </span>
            </div>
          }
        />
      </div>
      <div className="flex flex-row flex-nowrap gap-3 w-full">
        {(isLoadingTotalBudget || totalBudget?.title.length > 0) && (
          <AsyncStatsCard
            title={totalBudget.title}
            color="bg-chrysler-blue/20"
            content={
              <span className="font-bold">${formatNumber(totalBudget?.data)}</span>
            }
            isLoading={isLoadingTotalBudget}
          />
        )}
        <AsyncStatsCard
          title={'Active Campaigns'}
          color="bg-light-cyan"
          content={
            <span className="font-bold">{Number(campaignsList?.data?.length || 0)}</span>
          }
          isLoading={isLoadingCampaigns}
        />
        <AsyncStatsCard
          title={`Primary KPI - ${primaryKpi.kpi}`}
          color="bg-primary-persian-blue/10"
          content={<span className="font-bold">{formatNumber(primaryKpi?.data)}</span>}
          isLoading={isLoadingPrimaryKpi}
        />
      </div>
      <div className="grid grid-cols-2 gap-5">
        <ImpressionLineChart
          data={metricsData?.data}
          isLoading={isLoadingMetrics}
          sheetTitle={'Impressions'}
          isPrevious={false}
        />
        <ClickLineChart
          data={metricsData?.data}
          isLoading={isLoadingMetrics}
          sheetTitle={'Clicks'}
          isPrevious={false}
        />
      </div>

      <div className="grid grid-cols-3 gap-5">
        {isLoadingTotalCustomers || totalCustomers?.data > 0 ? (
          <AsyncStatsCard
            title={'Total Customers'}
            content={
              <span className="font-bold">{formatNumber(totalCustomers?.data)}</span>
            }
            isLoading={isLoadingTotalCustomers}
          />
        ) : (
          <div
            className="flex flex-row border-2 border-dashed border-primary-persian-blue/10 bg-primary-persian-blue/5 rounded-md flex justify-between items-center cursor-pointer p-3"
            onClick={() =>
              navigate(t('dealership.customers.sources.create', { dealership }))
            }>
            <div className="flex flex-col gap-3 opacity-50">
              <span className="text-xs text-primary-xiketic">Total Customers</span>
              <span className="font-bold text-primary-xiketic">
                {formatNumber(totalCustomers?.data)}
              </span>
            </div>
            <span className="font-bold text-primary-persian-blue text-xs">
              + Add Your Customers
            </span>
          </div>
        )}
        <AsyncStatsCard
          title={'Total Audiences'}
          content={
            <span className="font-bold">{formatNumber(totalAudiences?.data)}</span>
          }
          isLoading={isLoadingTotalAudiences}
        />
        <AsyncStatsCard
          title={'Phone Calls'}
          content={<span className="font-bold">{formatNumber(phoneCalls?.data)}</span>}
          isLoading={isLoadingPhoneCalls}
        />
        {isLoadingInventoryTotals || inventoryTotals?.total > 0 ? (
          <AsyncStatsCard
            title={'Cars in Inventory'}
            content={
              <span className="font-bold">{formatNumber(inventoryTotals?.total)}</span>
            }
            isLoading={isLoadingInventoryTotals}
          />
        ) : (
          <div
            className="flex flex-row border-2 border-dashed border-primary-persian-blue/10 bg-primary-persian-blue/5 rounded-md flex justify-between items-center cursor-pointer p-3"
            onClick={() =>
              navigate(t('dealership.customers.sources.create', { dealership }))
            }>
            <div className="flex flex-col gap-3 opacity-50">
              <span className="text-xs text-primary-xiketic">Cars in Inventory</span>
              <span className="font-bold text-primary-xiketic">
                {formatNumber(inventoryTotals?.total)}
              </span>
            </div>
            <span className="font-bold text-primary-persian-blue text-xs">
              + Add Your Inventory
            </span>
          </div>
        )}
        <AsyncStatsCard
          title={'New Cars in Inventory'}
          content={
            <span className="font-bold">{formatNumber(inventoryTotals?.used)}</span>
          }
          isLoading={isLoadingInventoryTotals}
        />
        <AsyncStatsCard
          title={'Used Cars in Inventory'}
          content={
            <span className="font-bold">{formatNumber(inventoryTotals?.new)}</span>
          }
          isLoading={isLoadingInventoryTotals}
        />
      </div>
      <div className="flex flex-col gap-3 pt-5">
        <div className="flex flex-row justify-between items center">
          <span className="font-bold">Campaigns</span>
          <Button
            animate={false}
            variant="secondary"
            onClick={() => navigate(t('dealership.campaigns.list', { dealership }))}>
            See Campaign Level Reporting
          </Button>
        </div>
        <div className="grid grid-cols-3 w-full gap-5">
          {campaignsList?.data?.slice(0, 3).map((campaign) => (
            <AsyncCampaignCard
              key={campaign.flight_id}
              campaign={campaign}
              vendor_icon={
                <div className="flex max-w-[40px] object-contain rounded-full">
                  {productCodeToVendorIcon(campaign?.product_code)}
                </div>
              }
              isLoading={isLoadingCampaigns}
              to={t('dealership.campaigns.report', { dealership, campaign })}
              show_budget_type={agency.data?.show_budget_type}
            />
          ))}
          {!isLoadingCampaigns &&
            Array.from({ length: 3 - Number(campaignsList?.data?.length || 0) }).map(
              (_, idx) => (
                <CampaignSuggestion
                  key={`suggestion_${idx}`}
                  index={idx}
                  to={t('dealership.campaigns.create', { dealership })}
                />
              ),
            )}
        </div>
      </div>
      <div className="flex flex-col gap-3 pt-5">
        <div className="flex flex-row justify-between items center">
          <span className="font-bold">Sources</span>
          <Button
            animate={false}
            variant="secondary"
            onClick={() =>
              navigate(t('dealership.customers.sources.create', { dealership }))
            }>
            Add more sources
          </Button>
        </div>
        <div className="grid grid-cols-3 w-full gap-5">
          {sourcesList.sources?.slice(0, 3).map((source, idx) => (
            <div key={`${source.id}-${idx}`}>
              <AsyncSourceCard
                title={source.customer_source}
                last_updated={source.updated_date}
                total_customers={source.total_customers}
                id={source.id}
                isLoading={isLoadingSources}
                to={t('dealership.customers.sources.report', { dealership, source })}
              />
            </div>
          ))}
          {!isLoadingSources &&
            Array.from({ length: 3 - sourcesList?.sources?.length }).map((_, idx) => (
              <SourceSuggestion
                key={`suggestions${idx}`}
                index={idx}
                to={t('dealership.customers.sources.create', { dealership })}
              />
            ))}
        </div>
      </div>
      <Outlet />
    </div>
  )
}
